import type { WorkplaceReservation } from '../services/floorplan-editor.service';

export interface ReservationClipboardLabels {
  readonly date: string;
  readonly name: string;
  readonly email: string;
  readonly morning: string;
  readonly afternoon: string;
  readonly source: string;
  readonly booked: string;
  readonly notBooked: string;
  readonly unknownUser: string;
  readonly sourceReservation: string;
  readonly sourceGroup: string;
  readonly sourceCheckIn: string;
}

/**
 * The reservations as tab-separated rows, ready to paste into a spreadsheet.
 *
 * Tabs rather than commas, matching the employee-presence copy: a tab-separated block pastes
 * straight into Excel or Sheets as columns, while a CSV pasted into a cell stays one long
 * string until somebody runs text-to-columns on it. Nothing is quoted or escaped because
 * nothing here can contain a tab or a newline — the fields are a date, a person's name, an
 * email address and three fixed words.
 *
 * The source column is included even though the table only chips two of the three: on screen
 * an unlabelled row plainly means "their own booking", but in a pasted block with no legend
 * a blank cell is a question.
 */
export const reservationsToClipboardText = (
  reservations: readonly WorkplaceReservation[],
  labels: ReservationClipboardLabels,
  formatDate: (isoDate: string) => string,
): string => {
  const header = [
    labels.date,
    labels.name,
    labels.email,
    labels.morning,
    labels.afternoon,
    labels.source,
  ].join('\t');

  const sourceLabel = (reservation: WorkplaceReservation): string => {
    switch (reservation.source) {
      case 'GroupReservation':
        return labels.sourceGroup;
      case 'CheckIn':
        return labels.sourceCheckIn;
      default:
        return labels.sourceReservation;
    }
  };

  const rows = reservations.map((reservation) => {
    const name = [reservation.firstName, reservation.lastName].filter(Boolean).join(' ').trim();

    return [
      formatDate(reservation.date),
      name || labels.unknownUser,
      // An empty cell rather than the table's dash: a dash pasted into a spreadsheet is a
      // character somebody has to delete before filtering on the column.
      reservation.email ?? '',
      reservation.morning ? labels.booked : labels.notBooked,
      reservation.afternoon ? labels.booked : labels.notBooked,
      sourceLabel(reservation),
    ].join('\t');
  });

  return [header, ...rows].join('\n');
};

export default reservationsToClipboardText;
